import { DEFAULT_BOOKING_ACCENT } from '@shared/core/booking-branding'
import type { BookingOnboardingDto } from '@shared/core/booking-onboarding'
import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import {
  bookingPageNameError,
  brandingFormFromSaved,
  buildBrandingFormData,
  isAcceptedLogoFile,
  isBrandingDirty,
  isSameSavedBranding,
  parseAccentInput,
  previewNameFor,
  readImageSize,
  savedBrandingFromDto,
  unsupportedFileMessage,
} from './booking-branding-card.domain'
import type { BrandingForm, BrandingMessage, SavedBranding } from './booking-branding-card.interface'

type SetForm = Dispatch<SetStateAction<BrandingForm>>

export function useBookingBrandingForm(bookingOnboarding: BookingOnboardingDto, accountName: string) {
  const [saved, setSaved] = useState<SavedBranding>(() => savedBrandingFromDto(bookingOnboarding, accountName))
  const [form, setForm] = useState<BrandingForm>(() => brandingFormFromSaved(saved))
  const [logoError, setLogoError] = useState<BrandingMessage | null>(null)
  const logoPicker = useLogoPicker(setForm, setLogoError)
  const accent = useAccentField(form.accentInput, setForm)
  const name = useNameField(form.name, form.name !== saved.name, setForm)
  const { clearBlur } = accent
  const { setServerError: setNameServerError } = name

  const replaceForm = useCallback(
    (next: BrandingForm) => {
      logoPicker.cancel()
      clearBlur()
      setLogoError(null)
      setForm(next)
    },
    [logoPicker, clearBlur],
  )
  const restoreSaved = useCallback(
    (next: SavedBranding) => {
      setNameServerError(null)
      replaceForm(brandingFormFromSaved(next))
    },
    [replaceForm, setNameServerError],
  )
  const applySaved = useCallback(
    (next: SavedBranding) => {
      setSaved(next)
      restoreSaved(next)
    },
    [restoreSaved],
  )

  useServerBrandingSync(savedBrandingFromDto(bookingOnboarding, accountName), saved, applySaved)
  useRevokeObjectUrl(form.logo?.kind === 'new' ? form.logo.objectUrl : null)

  return {
    form,
    accent,
    name,
    logoError,
    setLogoError,
    previewAccent: accent.isValid ? accent.value : saved.accent,
    previewName: previewNameFor(form.name, accountName),
    isDirty: isBrandingDirty(form, saved),
    pickLogo: logoPicker.pick,
    cancelLogoPick: logoPicker.cancel,
    removeLogo: () => replaceForm({ ...form, logo: null }),
    discard: () => restoreSaved(saved),
    resetToDefault: () => replaceForm({ ...form, accentInput: DEFAULT_BOOKING_ACCENT, logo: null }),
    buildFormData: () => buildBrandingFormData(accent.value, form, saved),
    applySavedDto: (dto: BookingOnboardingDto) => applySaved(savedBrandingFromDto(dto, accountName)),
  }
}

function useNameField(value: string, isEdited: boolean, setForm: SetForm) {
  const [serverError, setServerError] = useState<BrandingMessage | null>(null)
  // An untouched name is never sent, so a prefilled account name over the limit must not block Save.
  const validationError = isEdited ? bookingPageNameError(value) : null

  return {
    isValid: !validationError,
    error: validationError ?? serverError,
    setServerError,
    change: (next: string) => {
      setServerError(null)
      setForm((current) => ({ ...current, name: next }))
    },
  }
}

function useAccentField(accentInput: string, setForm: SetForm) {
  const [blurred, setBlurred] = useState(false)
  const parsed = parseAccentInput(accentInput)
  const value = 'value' in parsed ? parsed.value : null
  const isValid = !('error' in parsed)
  const clearBlur = useCallback(() => setBlurred(false), [])
  const lastValidAccent = useRef(DEFAULT_BOOKING_ACCENT)
  if (isValid) lastValidAccent.current = value ?? DEFAULT_BOOKING_ACCENT

  return {
    value,
    isValid,
    showError: !isValid && blurred,
    selectedSwatch: isValid ? (value ?? DEFAULT_BOOKING_ACCENT) : null,
    pickerColor: lastValidAccent.current,
    clearBlur,
    select: (hex: string) => {
      setBlurred(false)
      setForm((current) => ({ ...current, accentInput: hex }))
    },
    change: (next: string) => setForm((current) => ({ ...current, accentInput: next })),
    blur: () => {
      setBlurred(true)
      if (isValid) setForm((current) => ({ ...current, accentInput: value ?? DEFAULT_BOOKING_ACCENT }))
    },
  }
}

function useLogoPicker(setForm: SetForm, setLogoError: (message: BrandingMessage | null) => void) {
  const latestPick = useRef(0)
  useEffect(() => {
    const picks = latestPick
    return () => {
      picks.current += 1
    }
  }, [])

  return useRef({
    cancel: () => {
      latestPick.current += 1
    },
    pick: async (file: File) => {
      const pickId = ++latestPick.current
      const isCurrent = () => pickId === latestPick.current
      if (!isAcceptedLogoFile(file)) {
        setLogoError(unsupportedFileMessage(file.name))
        return
      }
      const objectUrl = URL.createObjectURL(file)
      const size = await readImageSize(objectUrl).catch(() => null)
      if (!isCurrent() || !size) {
        URL.revokeObjectURL(objectUrl)
        if (isCurrent()) setLogoError(unsupportedFileMessage(file.name))
        return
      }
      setLogoError(null)
      setForm((current) => ({ ...current, logo: { kind: 'new', file, objectUrl, ...size } }))
    },
  }).current
}

function useServerBrandingSync(server: SavedBranding, saved: SavedBranding, applySaved: (next: SavedBranding) => void) {
  const { name, accent, logoUrl } = server
  const savedRef = useRef(saved)
  savedRef.current = saved

  useEffect(() => {
    const next = { name, accent, logoUrl }
    if (isSameSavedBranding(savedRef.current, next)) return
    applySaved(next)
  }, [name, accent, logoUrl, applySaved])
}

function useRevokeObjectUrl(objectUrl: string | null) {
  useEffect(() => {
    if (!objectUrl) return undefined
    return () => URL.revokeObjectURL(objectUrl)
  }, [objectUrl])
}
